import React, { useEffect, useRef, useMemo } from "react";
import { ResizablePanelHandle } from "./ResizablePanelHandle";
import { ChatbotHeader } from "./ChatbotHeader";
import { ChatIntroSection } from "./ChatIntroSection";
import { ChatbotTabs } from "./ChatbotTabs";
import { UserContextTab } from "./UserContextTab";
import { ChatHistoryTab } from "./ChatHistoryTab";
import { Box, Button } from "../app/material-framework/materialComponents";
import { ChatbotCheckboxes } from "./ChatbotCheckboxes";
import { ChatMessage } from "./ChatMessage";
import { ChatInputArea } from "./ChatInputArea";
import ChatLoadingWidget from "./ChatLoadingWidget";
import { features } from "../app/features";

export function ChatbotComponents({ controller, isPopout, onTogglePopout, onClose, onWidthDrag }) {
  const {
    fullscreen,
    showIntroSection,
    topQuestions,
    handleNewChat,
    setShowIntroSection,
    handleSend,
    handleKeyDown,
    messages,
    formatTimestamp,
    feedback,
    setFeedback,
    loading,
    showContinuePrompt,
    handleContinueYes,
    handleContinueNo,
    input,
    setInput,
    tab,
    setTab,
    reportSummaryInput,
    reportSummaryError,
    reportSummaryData,
    reportSummaryLoading,
    handleReportSummaryChange,
    handleGetSummary,
    userContext,
    selectedOptions,
    setSelectedOptions,
    deepSearch,
    setDeepSearch,
    setFullscreen,
    onToggleTheme,
    isHtmlReport,
    handleOpenConversation,
    chatHistory,
    chatHistoryLoading,
    chatHistoryError,
    fetchChatHistory,
    handleSummarizeReport,
  } = controller;
  const userMessageRefs = useRef([]);

  const tabLabels = useMemo(() => {
    const labels = ["Chat", "Summary"];
    if (features.chatbot.showChatHistory) {
      labels.push("Chat History");
    }
    return labels;
  }, []);

  useEffect(() => {
    const lastUserIdx = messages.map((m) => m.from).lastIndexOf("user");
    if (lastUserIdx !== -1 && userMessageRefs.current[lastUserIdx]) {
      userMessageRefs.current[lastUserIdx].scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [messages]);

  const noneSelected = !(
    selectedOptions.all ||
    selectedOptions.economics ||
    selectedOptions.fx ||
    selectedOptions.fi ||
    selectedOptions.eq
  );

  return (
    <>
      {!fullscreen && !isPopout && onWidthDrag && <ResizablePanelHandle onDrag={onWidthDrag} />}
      <ChatbotHeader
        onNewChat={handleNewChat}
        onInfoClick={() => setShowIntroSection((p) => !p)}
        onTogglePopout={onTogglePopout}
        onToggleFullscreen={() => setFullscreen((f) => !f)}
        onClose={onClose}
        fullscreen={fullscreen}
        isPopout={isPopout}
        onToggleTheme={onToggleTheme}
        isLoading={loading}
        isHtmlReport={isHtmlReport}
      />
      {showIntroSection && <ChatIntroSection onClose={() => setShowIntroSection(false)} isHtmlReport={isHtmlReport} />}
      <ChatbotTabs tab={tab} setTab={setTab} tabLabels={tabLabels} />
      {tab === 2 && features.chatbot.showChatHistory ? (
        <ChatHistoryTab
          onOpenConversation={handleOpenConversation}
          onRerunQuestion={(question) => {
            setTab(0);
            handleSend(question);
          }}
          conversations={chatHistory}
          loading={chatHistoryLoading}
          error={chatHistoryError}
          onRetry={fetchChatHistory}
        />
      ) : tab === 1 ? (
        <UserContextTab
          input={reportSummaryInput}
          error={reportSummaryError}
          summary={reportSummaryData}
          loading={reportSummaryLoading}
          onInputChange={handleReportSummaryChange}
          onGetSummary={handleGetSummary}
          disabled={!userContext.trim()}
        />
      ) : (
        <>
          <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "#EFEFEF" }}>
            <ChatbotCheckboxes
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              deepSearch={deepSearch}
              setDeepSearch={setDeepSearch}
            />
            <Box sx={{ paddingY: 2 }}>
              <ChatMessage
                key={`bot-00001-${new Date()}`}
                msg={{
                  from: "bot",
                  text: "Hello! How can I help you? Here are a few sample questions to get you started",
                  timestamp: messages.length > 0 ? messages[0].timestamp : new Date(),
                }}
                fullscreen={fullscreen}
                formatTimestamp={formatTimestamp}
                showFeedback={false}
              />
              {topQuestions.map((q) => (
                <Button
                  key={q.label}
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1, mb: 1, borderRadius: 2, textTransform: "none" }}
                  disabled={loading || noneSelected}
                  onClick={() => {
                    if (q.isSummary) {
                      handleSummarizeReport();
                    } else {
                      handleSend(q.value, q.label);
                    }
                  }}
                >
                  {q.label}
                </Button>
              ))}
            </Box>
            {messages.map((msg, idx) => (
              <ChatMessage
                key={`${msg.from}-${idx}-${msg.timestamp}`}
                msg={msg}
                conv_id={msg.conv_id}
                idx={idx}
                fullscreen={fullscreen}
                formatTimestamp={formatTimestamp}
                feedback={feedback}
                setFeedback={setFeedback}
                showFeedback={!msg.historical}
                ref={(el) => {
                  if (msg.from === "user") userMessageRefs.current[idx] = el;
                }}
              />
            ))}
            {loading && <ChatLoadingWidget />}
          </Box>
          <ChatInputArea
            showContinuePrompt={showContinuePrompt}
            onContinueYes={handleContinueYes}
            onContinueNo={handleContinueNo}
            input={input}
            onInputChange={(e) => setInput(e.target.value)}
            onSend={handleSend}
            onKeyDown={handleKeyDown}
            loading={loading || noneSelected}
            isPopout={isPopout}
          />
        </>
      )}
    </>
  );
}
